// src/components/tickets/TicketRow.tsx
//
// One ticket row in a desk view. Verbatim spec (EXPERIENCE.md → Ticket row):
// "Click anywhere opens detail. Shows: question (truncated), type, brand,
// status badge, route chip, assignee, age. ANSWERED rows badged distinctly.
// Flags render inline." Clicking anywhere navigates (useNavigate); the truncated
// question is ALSO a real Link — the keyboard/SR-focusable equivalent of the
// row click. Display vocabulary comes entirely from ticketDisplay.ts.

import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { Badge } from '../ui/Badge'
import { formatRelativeTime, formatDateTime } from '../../utils/dateUtils'
import {
  ticketStatusBadge,
  routeChip,
  ticketFlags,
  questionTypeLabel,
  ticketBrand,
  assigneeName,
} from '../../utils/ticketDisplay'
import type { YakTicketWithAssignee } from '../../types/yak'

const tdClass = 'px-6 py-4 text-sm text-gray-900 align-top'

// The route chip is a small OUTLINED pill — subordinate to the filled status
// badge (DESIGN.md: provenance metadata, not a call to action).
function RouteChip({ route }: { route: YakTicketWithAssignee['route_label'] }) {
  const { label, tone } = routeChip(route)
  const toneClass =
    tone === 'green'
      ? 'border-green-300 text-green-700'
      : 'border-gray-300 text-gray-600'
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${toneClass}`}
    >
      {label}
    </span>
  )
}

export function TicketRow({ ticket }: { ticket: YakTicketWithAssignee }) {
  const navigate = useNavigate()
  const detailPath = `/tickets/${ticket.id}`
  const status = ticketStatusBadge(ticket.status)
  const flags = ticketFlags(ticket)
  const created = ticket.created_at

  return (
    <tr
      onClick={() => navigate(detailPath)}
      className="hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <td className="px-6 py-4 text-sm text-gray-900 align-top max-w-md">
        <Link
          to={detailPath}
          onClick={e => e.stopPropagation()}
          className="text-gray-900 hover:text-brand-600 hover:underline line-clamp-2"
        >
          {ticket.question}
        </Link>
        {flags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {flags.map(flag => (
              <Badge key={flag.key} variant="red">
                <AlertTriangle className="w-3 h-3 mr-1" aria-hidden="true" />
                {flag.label}
              </Badge>
            ))}
          </div>
        )}
      </td>
      <td className={`${tdClass} whitespace-nowrap`}>
        {questionTypeLabel(ticket.question_type)}
      </td>
      <td className={`${tdClass} whitespace-nowrap`}>{ticketBrand(ticket)}</td>
      <td className={`${tdClass} whitespace-nowrap`}>
        <Badge variant={status.variant}>{status.label}</Badge>
      </td>
      <td className={`${tdClass} whitespace-nowrap`}>
        <RouteChip route={ticket.route_label} />
      </td>
      <td className={`${tdClass} whitespace-nowrap`}>{assigneeName(ticket)}</td>
      <td
        className={`${tdClass} whitespace-nowrap text-gray-500`}
        title={created ? formatDateTime(created) : undefined}
      >
        {created ? formatRelativeTime(created) : '—'}
      </td>
    </tr>
  )
}
